/* eslint-disable */
import { useState, useRef, useEffect, useCallback } from "react";
import { saveToStorage, loadFromStorage, getSupabaseClient } from "./Auth";
import { exportSetPiecePDF, ExportButton } from "./ExportPDF";

/* ── Cloud sync helpers ── */
async function saveRoutinesToCloud(routines) {
  try {
    const sb = getSupabaseClient();
    if (!sb) return;
    const { data: { user } } = await sb.auth.getUser();
    if (!user) return;
    const row = {
      user_id: user.id, name: "__set_piece_routines__", formation: "N/A",
      tokens: routines, arrows: [],
      notes: { type: "set_piece_routines", count: routines.length },
      updated_at: new Date().toISOString(),
    };
    const existing = await sb.from("tactics_boards").select("id")
      .eq("user_id", user.id).eq("name", "__set_piece_routines__").single();
    if (existing.data?.id) {
      await sb.from("tactics_boards").update(row).eq("id", existing.data.id);
    } else {
      await sb.from("tactics_boards").insert([row]);
    }
  } catch(e) { console.warn("Set piece cloud save:", e.message); }
}

async function loadRoutinesFromCloud() {
  try {
    const sb = getSupabaseClient();
    if (!sb) return null;
    const { data: { user } } = await sb.auth.getUser();
    if (!user) return null;
    const { data } = await sb.from("tactics_boards")
      .select("tokens").eq("user_id", user.id).eq("name", "__set_piece_routines__").single();
    return data?.tokens || null;
  } catch(e) { return null; }
}

/* ═══════════════════════════════════════════════════════════
   FOOTY ANALYST — SET PIECE BUILDER
   Design corners, free kicks and throw-ins
   Assign runners · Mark delivery zones · Save routines
═══════════════════════════════════════════════════════════ */

const SP_CSS = `
.sp-wrap { font-family:'DM Sans',sans-serif; background:var(--cream); height:calc(100vh - 58px); display:grid; grid-template-columns:255px 1fr 275px; overflow:hidden; }
@media(max-width:900px) {
  .sp-wrap { grid-template-columns:1fr; height:auto; overflow:auto; }
  .sp-left { max-height:280px; overflow-y:auto; border-right:none; border-bottom:2px solid var(--rule); }
  .sp-canvas-area { height:340px; flex-shrink:0; }
  .sp-right { max-height:380px; border-left:none; border-top:2px solid var(--rule); }
  .sp-toolbar { flex-wrap:wrap; gap:0.3rem; }
  .sp-runner-types { flex-direction:row; flex-wrap:wrap; gap:0.25rem; }
  .sp-runner-row { width:calc(50% - 0.15rem); }
}
.sp-left { background:var(--white); border-right:2px solid var(--rule); display:flex; flex-direction:column; overflow:hidden; }
.sp-left-header { background:var(--teal2); padding:0.85rem 1rem; border-bottom:2px solid var(--mustard); flex-shrink:0; }
.sp-left-title { font-family:'Bebas Neue',sans-serif; font-size:1rem; letter-spacing:2.5px; color:var(--white); }
.sp-left-sub { font-size:0.58rem; color:rgba(255,255,255,0.55); font-weight:300; margin-top:0.12rem; }
.sp-section { padding:0.75rem 1rem; border-bottom:1.5px solid var(--rule); flex-shrink:0; }
.sp-section-title { font-size:0.56rem; font-weight:800; letter-spacing:2px; text-transform:uppercase; color:var(--muted); margin-bottom:0.5rem; }
.sp-grid-2 { display:grid; grid-template-columns:1fr 1fr; gap:0.32rem; }
.sp-btn { padding:0.4rem 0.45rem; text-align:center; font-family:'DM Sans',sans-serif; font-size:0.62rem; font-weight:800; letter-spacing:1px; text-transform:uppercase; cursor:pointer; border:1.5px solid var(--rule); color:var(--muted); background:transparent; transition:all 0.18s; line-height:1.3; }
.sp-btn:hover { border-color:var(--teal); color:var(--teal); }
.sp-btn.on { background:var(--teal); border-color:var(--teal); color:var(--white); }
.sp-btn.on-red { background:var(--darkred); border-color:var(--darkred); color:var(--white); }
.sp-mode-row { display:flex; border:1.5px solid var(--rule); }
.sp-mode-btn { flex:1; padding:0.38rem; text-align:center; font-family:'DM Sans',sans-serif; font-size:0.62rem; font-weight:800; letter-spacing:1px; text-transform:uppercase; cursor:pointer; color:var(--muted); transition:all 0.18s; border-right:1px solid var(--rule); }
.sp-mode-btn:last-child { border-right:none; }
.sp-mode-btn.atk { background:var(--teal); color:var(--white); }
.sp-mode-btn.def { background:var(--darkred); color:var(--white); }
.sp-runner-row { display:flex; align-items:center; gap:0.55rem; padding:0.38rem 0.6rem; border:1.5px solid var(--rule); margin-bottom:0.28rem; cursor:pointer; transition:all 0.18s; }
.sp-runner-row:hover { border-color:var(--teal); }
.sp-runner-row.on { border-left-width:3px; }
.sp-runner-dot { width:12px; height:12px; border-radius:50%; flex-shrink:0; border:2px solid rgba(0,0,0,0.15); }
.sp-runner-label { font-size:0.7rem; font-weight:700; color:var(--ink); }
.sp-runner-sub { font-size:0.58rem; color:var(--muted); font-weight:300; }
.sp-routine-scroll { flex:1; overflow-y:auto; padding:0.5rem; }
.sp-routine-item { display:flex; align-items:center; gap:0.5rem; padding:0.48rem 0.6rem; border:1.5px solid var(--rule); margin-bottom:0.3rem; cursor:pointer; transition:all 0.18s; background:var(--cream2); }
.sp-routine-item:hover { border-color:var(--teal); background:var(--teal4); }
.sp-routine-item.active { border-color:var(--teal); background:var(--teal4); border-left:3px solid var(--teal); }
.sp-routine-name { font-size:0.76rem; font-weight:700; color:var(--ink); flex:1; }
.sp-routine-meta { font-size:0.58rem; color:var(--muted); font-style:italic; }
.sp-canvas-area { display:flex; flex-direction:column; background:var(--ink); }
.sp-toolbar { background:rgba(26,22,18,0.95); border-bottom:2px solid var(--mustard); padding:0.45rem 0.9rem; display:flex; align-items:center; gap:0.45rem; flex-wrap:wrap; flex-shrink:0; }
.sp-tb-grp { display:flex; gap:0.3rem; align-items:center; padding-right:0.6rem; border-right:1px solid rgba(255,255,255,0.1); }
.sp-tb-grp:last-child { border-right:none; margin-left:auto; }
.sp-tb-lbl { font-size:0.52rem; font-weight:800; letter-spacing:2px; text-transform:uppercase; color:rgba(255,255,255,0.3); margin-right:0.08rem; }
.sp-tb-b { padding:0.26rem 0.58rem; font-family:'DM Sans',sans-serif; font-size:0.6rem; font-weight:800; letter-spacing:1px; text-transform:uppercase; background:transparent; border:1px solid rgba(255,255,255,0.18); color:rgba(255,255,255,0.6); cursor:pointer; transition:all 0.18s; }
.sp-tb-b:hover { border-color:var(--mustard); color:var(--mustard); }
.sp-tb-b.on { background:var(--teal); border-color:var(--teal); color:var(--white); }
.sp-tb-b.red { color:rgba(248,113,113,0.8); border-color:rgba(139,42,42,0.4); }
.sp-tb-b.red:hover { background:var(--darkred); border-color:var(--darkred); color:var(--white); }
.sp-pitch-wrap { flex:1; position:relative; overflow:hidden; }
.sp-canvas { position:absolute; inset:0; display:block; }
.sp-right { background:var(--white); border-left:2px solid var(--rule); display:flex; flex-direction:column; overflow:hidden; }
.sp-right-head { background:var(--cream2); padding:0.8rem 1rem; border-bottom:1.5px solid var(--rule); flex-shrink:0; font-family:'Bebas Neue',sans-serif; font-size:0.9rem; letter-spacing:2px; color:var(--ink); }
.sp-right-body { flex:1; overflow-y:auto; padding:0.85rem; }
.sp-r-label { display:block; font-size:0.56rem; font-weight:800; letter-spacing:2px; text-transform:uppercase; color:var(--muted); margin-bottom:0.3rem; margin-top:0.6rem; }
.sp-r-label:first-child { margin-top:0; }
.sp-r-input { width:100%; background:var(--cream); border:1.5px solid var(--rule); border-bottom:2px solid var(--teal); padding:0.52rem 0.75rem; color:var(--ink); font-family:'DM Sans',sans-serif; font-size:0.8rem; font-weight:500; outline:none; margin-bottom:0; }
.sp-r-select { width:100%; background:var(--cream); border:1.5px solid var(--rule); border-bottom:2px solid var(--teal); padding:0.52rem 0.75rem; color:var(--ink); font-family:'DM Sans',sans-serif; font-size:0.8rem; font-weight:500; outline:none; cursor:pointer; }
.sp-zone-opt { display:flex; align-items:flex-start; gap:0.55rem; padding:0.5rem 0.75rem; border:1.5px solid var(--rule); margin-bottom:0.35rem; cursor:pointer; transition:all 0.18s; border-left-width:3px; border-left-color:transparent; }
.sp-zone-opt:hover { background:var(--teal4); }
.sp-zone-opt.on { background:var(--mustard3); border-left-color:var(--mustard); }
.sp-zone-title { font-size:0.78rem; font-weight:800; color:var(--ink); }
.sp-zone-desc { font-size:0.68rem; color:var(--muted); font-style:italic; font-weight:300; line-height:1.55; }
.sp-legend-row { display:flex; align-items:center; gap:0.48rem; padding:0.3rem 0.45rem; cursor:pointer; border-radius:2px; transition:all 0.15s; font-size:0.72rem; }
.sp-legend-row:hover { background:var(--teal4); }
.sp-legend-row.sel { background:var(--teal4); }
.sp-legend-dot { width:11px; height:11px; border-radius:50%; flex-shrink:0; }
.sp-save-box { background:var(--cream2); border:1.5px solid var(--rule); padding:0.8rem; margin-top:0.75rem; }
.sp-save-inp { width:100%; background:var(--white); border:1.5px solid var(--rule); border-bottom:2px solid var(--ink); padding:0.5rem 0.7rem; color:var(--ink); font-family:'DM Sans',sans-serif; font-size:0.8rem; font-weight:500; outline:none; margin-bottom:0.55rem; }
.sp-save-inp:focus { border-bottom-color:var(--teal); }
.sp-save-btn { width:100%; padding:0.68rem; background:var(--teal); color:var(--white); border:2px solid var(--teal); font-family:'DM Sans',sans-serif; font-weight:800; font-size:0.72rem; letter-spacing:2px; text-transform:uppercase; cursor:pointer; transition:all 0.2s; }
.sp-save-btn:hover { background:var(--teal2); }
.sp-export-btn { width:100%; padding:0.6rem; background:transparent; color:var(--ink); border:1.5px solid var(--rule); font-family:'DM Sans',sans-serif; font-weight:700; font-size:0.68rem; letter-spacing:1.5px; text-transform:uppercase; cursor:pointer; margin-top:0.38rem; }
.sp-export-btn:hover { border-color:var(--teal); color:var(--teal); }
.sp-sum-row { display:flex; justify-content:space-between; align-items:center; padding:0.38rem 0; border-bottom:1px solid var(--rule2); font-size:0.73rem; }
.sp-sum-lbl { color:var(--muted); }
.sp-sum-val { color:var(--ink); font-weight:800; }
`;

const SP_TYPES = [
  {id:"corner-right",label:"Corner Right",icon:"⚽"},{id:"corner-left",label:"Corner Left",icon:"⚽"},
  {id:"freekick",label:"Free Kick",icon:"🎯"},{id:"throwin",label:"Throw-in",icon:"🤲"},
  {id:"goalkick",label:"Goal Kick",icon:"🧤"},{id:"penalty",label:"Penalty",icon:"⚡"},
];

const RUNNER_TYPES = [
  {id:"near",   label:"Near Post",    desc:"Near post run",    color:"#f59e0b"},
  {id:"far",    label:"Far Post",     desc:"Far post run",     color:"#3b82f6"},
  {id:"pen",    label:"Penalty Spot", desc:"Pen spot arrival", color:"#8b5cf6"},
  {id:"edge",   label:"Edge of Box",  desc:"2nd ball",         color:"#16a34a"},
  {id:"dummy",  label:"Dummy Runner", desc:"Creates space",    color:"#94a3b8"},
  {id:"taker",  label:"Delivery",     desc:"Takes the set piece",color:"#ef4444"},
  {id:"block",  label:"Block/Screen", desc:"Blocks defender",  color:"#f97316"},
  {id:"cover",  label:"Cover",        desc:"Counter cover",    color:"#0891b2"},
];

const DELIVERY_ZONES = {
  "corner-right":[{id:"near",label:"Near Post",desc:"Driven to near post — flick-on"},{id:"far",label:"Far Post",desc:"Floated to far post — aerial target"},{id:"pen",label:"Penalty Spot",desc:"Precise delivery to pen spot"},{id:"short",label:"Short Corner",desc:"Short pass — breaks zonal block"},{id:"pullback",label:"Pull-back",desc:"Short then cross from new angle"}],
  "corner-left": [{id:"near",label:"Near Post",desc:"Driven near post — right footer"},{id:"far",label:"Far Post",desc:"Inswinging to far post"},{id:"pen",label:"Penalty Spot",desc:"Most dangerous zone"},{id:"short",label:"Short Corner",desc:"Pull the block wider"},{id:"pullback",label:"Pull-back",desc:"Change the angle"}],
  "freekick":    [{id:"direct",label:"Direct",desc:"Attempt on goal"},{id:"near",label:"Near Area",desc:"Delivery to near post"},{id:"far",label:"Far Area",desc:"Delivery to far post"},{id:"pen",label:"Pen Spot",desc:"Whipped to pen spot"},{id:"short",label:"Short Pass",desc:"Pass then cross"}],
  "throwin":     [{id:"short",label:"Short",desc:"Short to feet"},{id:"long",label:"Long Throw",desc:"Into the box — treat as corner"}],
  "goalkick":    [{id:"short",label:"Short Build",desc:"Play from back"},{id:"long",label:"Long Ball",desc:"Direct to striker"}],
  "penalty":     [{id:"placed",label:"Placed",desc:"Pick your corner"},{id:"power",label:"Power",desc:"Down the middle"}],
};

const PRESETS = [
  {id:"near-flick",name:"Near Post Flick",type:"corner-right",mode:"attack",deliveryZone:"near",desc:"Classic near-post flick. Runner attacks near post, flick-on for arrivals.",tokens:[{id:1,x:90,y:93,type:"taker",num:7,name:""},{id:2,x:76,y:83,type:"near",num:9,name:""},{id:3,x:62,y:73,type:"far",num:10,name:""},{id:4,x:70,y:78,type:"pen",num:8,name:""},{id:5,x:55,y:89,type:"edge",num:6,name:""},{id:6,x:44,y:92,type:"cover",num:5,name:""}],arrows:[{id:1,x1:88,y1:91,x2:75,y2:82,color:"#f59e0b"},{id:2,x1:75,y1:82,x2:62,y2:72,color:"#3b82f6"},{id:3,x1:75,y1:82,x2:70,y2:77,color:"#8b5cf6"}]},
  {id:"far-post",name:"Far Post Target",type:"corner-left",mode:"attack",deliveryZone:"far",desc:"Inswinging delivery to far post. Tall target attacks back stick.",tokens:[{id:1,x:10,y:93,type:"taker",num:11,name:""},{id:2,x:25,y:71,type:"far",num:9,name:""},{id:3,x:38,y:83,type:"near",num:10,name:""},{id:4,x:32,y:76,type:"pen",num:8,name:""},{id:5,x:48,y:90,type:"edge",num:6,name:""},{id:6,x:58,y:91,type:"cover",num:5,name:""}],arrows:[{id:1,x1:12,y1:91,x2:25,y2:72,color:"#3b82f6"},{id:2,x1:38,y1:82,x2:35,y2:73,color:"#f59e0b"}]},
  {id:"short-corner",name:"Short Corner",type:"corner-right",mode:"attack",deliveryZone:"short",desc:"Short pass to pull the block. Second player crosses from new angle.",tokens:[{id:1,x:90,y:93,type:"taker",num:7,name:""},{id:2,x:82,y:86,type:"near",num:11,name:""},{id:3,x:68,y:78,type:"pen",num:9,name:""},{id:4,x:60,y:72,type:"far",num:10,name:""},{id:5,x:55,y:87,type:"edge",num:8,name:""},{id:6,x:45,y:91,type:"cover",num:6,name:""}],arrows:[{id:1,x1:88,y1:91,x2:82,y2:86,color:"#ef4444"},{id:2,x1:82,y1:86,x2:68,y2:77,color:"#8b5cf6"}]},
  {id:"zonal-def",name:"Zonal Defence",type:"corner-right",mode:"defend",deliveryZone:"near",desc:"Zonal marking. Each player owns a zone. GK claims everything possible.",tokens:[{id:1,x:50,y:93,type:"cover",num:1,name:"GK"},{id:2,x:79,y:83,type:"block",num:2,name:""},{id:3,x:67,y:78,type:"block",num:5,name:""},{id:4,x:57,y:76,type:"block",num:6,name:""},{id:5,x:46,y:78,type:"block",num:4,name:""},{id:6,x:37,y:75,type:"block",num:3,name:""},{id:7,x:60,y:91,type:"edge",num:8,name:""},{id:8,x:44,y:93,type:"cover",num:10,name:""}],arrows:[]},
];

const TOKEN_R = 17;

function rStyle(type){return RUNNER_TYPES.find(r=>r.id===type)||RUNNER_TYPES[0];}

export default function SetPieceBuilder({squad=[]}){
  const canvasRef = useRef(null);
  const wrapRef   = useRef(null);
  const [size, setSize] = useState({w:600,h:500});
  const [spType, setSpType] = useState("corner-right");
  const [mode, setMode]   = useState("attack");
  const [tool, setTool]   = useState("move");
  const [selRunner, setSelRunner] = useState("near");
  const [tokens, setTokens] = useState([]);
  const [arrows, setArrows] = useState([]);
  const [zone, setZone]   = useState(null);
  const [selTok, setSelTok] = useState(null);
  const [arrowStart, setArrowStart] = useState(null);
  const [arrowColor, setArrowColor] = useState("#FFD700");
  const [dragging, setDragging] = useState(false);
  const [routines, setRoutines] = useState(()=>loadFromStorage("sp_routines",PRESETS));
  const [activeId, setActiveId] = useState(null);
  const [rName, setRName] = useState("");
  const [rDesc, setRDesc]  = useState("");
  const [syncStatus, setSyncStatus] = useState("");

  // Load from cloud on mount — merge with local
  useEffect(()=>{
    loadRoutinesFromCloud().then(cloudRoutines=>{
      if(cloudRoutines && cloudRoutines.length > 0) {
        setRoutines(prev=>{
          const merged = [...prev];
          cloudRoutines.forEach(cr=>{
            if(!merged.find(r=>r.id===cr.id)) merged.push(cr);
          });
          saveToStorage("sp_routines", merged);
          return merged;
        });
      }
    });
  },[]);

  useEffect(()=>{
    const el = wrapRef.current;
    if(!el) return;
    const obs = new ResizeObserver(e=>{
      const r = e[0].contentRect;
      setSize({w:Math.floor(r.width),h:Math.floor(r.height)});
    });
    obs.observe(el);
    return ()=>obs.disconnect();
  },[]);

  // ── DRAW ──
  useEffect(()=>{
    const cv = canvasRef.current;
    if(!cv) return;
    const ctx = cv.getContext("2d");
    const {w,h} = size;
    cv.width=w; cv.height=h;
    const px=(xp,yp)=>({x:(xp/100)*w, y:(yp/100)*h});

    // Pitch bg
    const g = ctx.createLinearGradient(0,0,0,h);
    g.addColorStop(0,"#2d5a32"); g.addColorStop(.35,"#357a3d");
    g.addColorStop(.5,"#2d6035"); g.addColorStop(.7,"#357a3d"); g.addColorStop(1,"#2d5a32");
    ctx.fillStyle=g; ctx.fillRect(0,0,w,h);

    // Stripes
    for(let i=0;i<8;i++){if(i%2===0){ctx.fillStyle="rgba(0,0,0,0.035)";ctx.fillRect(i*(w/8),0,w/8,h);}}

    ctx.strokeStyle="rgba(255,255,255,0.5)"; ctx.lineWidth=1.5;
    // Pitch outline
    ctx.beginPath(); ctx.rect(w*.04,h*.03,w*.92,h*.94); ctx.stroke();
    // Penalty area
    ctx.beginPath(); ctx.rect(w*.18,h*.03,w*.64,h*.40); ctx.stroke();
    // 6-yard box
    ctx.beginPath(); ctx.rect(w*.34,h*.03,w*.32,h*.15); ctx.stroke();
    // Goal
    ctx.strokeStyle="rgba(255,255,255,0.7)"; ctx.lineWidth=2.5;
    ctx.beginPath(); ctx.rect(w*.41,h*.03-h*.04,w*.18,h*.04); ctx.stroke();
    // Pen spot
    ctx.fillStyle="rgba(255,255,255,0.6)";
    const ps=px(50,26); ctx.beginPath(); ctx.arc(ps.x,ps.y,3,0,Math.PI*2); ctx.fill();
    // Pen arc
    ctx.strokeStyle="rgba(255,255,255,0.35)"; ctx.lineWidth=1.2;
    ctx.beginPath(); ctx.arc(ps.x,ps.y,w*.13,Math.PI*.18,Math.PI*.82); ctx.stroke();
    // Halfway dashed
    ctx.strokeStyle="rgba(255,255,255,0.2)"; ctx.lineWidth=1; ctx.setLineDash([8,5]);
    ctx.beginPath(); ctx.moveTo(w*.04,h*.72); ctx.lineTo(w*.96,h*.72); ctx.stroke();
    ctx.setLineDash([]);
    // Corner arcs
    ctx.strokeStyle="rgba(255,255,255,0.45)"; ctx.lineWidth=1.5;
    ctx.beginPath(); ctx.arc(w*.04,h*.03,w*.04,0,Math.PI/2); ctx.stroke();
    ctx.beginPath(); ctx.arc(w*.96,h*.03,w*.04,Math.PI/2,Math.PI); ctx.stroke();

    // Corner flag highlight
    const isR=spType==="corner-right", isL=spType==="corner-left";
    if(isR||isL){
      const cx=isR?w*.96:w*.04, cy=h*.96;
      ctx.fillStyle="rgba(212,148,26,0.35)";
      ctx.beginPath(); ctx.arc(cx,cy,14,0,Math.PI*2); ctx.fill();
      ctx.strokeStyle="#d4941a"; ctx.lineWidth=2;
      ctx.beginPath(); ctx.arc(cx,cy,14,0,Math.PI*2); ctx.stroke();
      ctx.fillStyle="#d4941a"; ctx.font="12px sans-serif";
      ctx.textAlign="center"; ctx.textBaseline="middle";
      ctx.fillText("⚽",cx,cy);
    }

    // Delivery zone
    if(zone){
      const zones={
        near:{x:.72,y:.08,w:.16,h:.20,c:"rgba(212,148,26,0.22)"},
        far: {x:.12,y:.08,w:.16,h:.20,c:"rgba(59,130,246,0.22)"},
        pen: {x:.38,y:.18,w:.24,h:.16,c:"rgba(139,92,246,0.22)"},
        short:{x:.70,y:.76,w:.20,h:.16,c:"rgba(239,68,68,0.22)"},
        pullback:{x:.68,y:.62,w:.22,h:.14,c:"rgba(22,163,74,0.22)"},
        direct:{x:.38,y:.03,w:.24,h:.14,c:"rgba(239,68,68,0.28)"},
        long:{x:.30,y:.10,w:.40,h:.22,c:"rgba(59,130,246,0.22)"},
        placed:{x:.38,y:.03,w:.24,h:.12,c:"rgba(239,68,68,0.28)"},
        power:{x:.42,y:.03,w:.16,h:.12,c:"rgba(239,68,68,0.28)"},
      };
      const z=zones[zone]||zones.near;
      ctx.fillStyle=z.c; ctx.strokeStyle=z.c.replace("0.22","0.7").replace("0.28","0.8");
      ctx.lineWidth=1.8; ctx.setLineDash([5,4]);
      ctx.beginPath(); ctx.rect(z.x*w,z.y*h,z.w*w,z.h*h); ctx.fill(); ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle="rgba(255,255,255,0.85)"; ctx.font="bold 10px DM Sans";
      ctx.textAlign="center"; ctx.textBaseline="middle";
      ctx.fillText("DELIVERY ZONE",(z.x+z.w/2)*w,(z.y+z.h/2)*h);
    }

    // Arrows
    arrows.forEach(a=>{
      const s=px(a.x1,a.y1), e=px(a.x2,a.y2);
      ctx.beginPath(); ctx.strokeStyle=a.color||"#FFD700"; ctx.lineWidth=2.5; ctx.lineCap="round";
      ctx.setLineDash(a.dashed?[7,4]:[]); ctx.moveTo(s.x,s.y); ctx.lineTo(e.x,e.y); ctx.stroke();
      ctx.setLineDash([]);
      const ang=Math.atan2(e.y-s.y,e.x-s.x), hl=12;
      ctx.beginPath(); ctx.strokeStyle=a.color||"#FFD700"; ctx.lineWidth=2;
      ctx.moveTo(e.x,e.y); ctx.lineTo(e.x-hl*Math.cos(ang-Math.PI/6),e.y-hl*Math.sin(ang-Math.PI/6));
      ctx.moveTo(e.x,e.y); ctx.lineTo(e.x-hl*Math.cos(ang+Math.PI/6),e.y-hl*Math.sin(ang+Math.PI/6));
      ctx.stroke();
    });

    // Tokens
    tokens.forEach(t=>{
      const p=px(t.x,t.y); const rs=rStyle(t.type); const isSel=selTok===t.id;
      ctx.fillStyle="rgba(0,0,0,0.28)"; ctx.beginPath(); ctx.arc(p.x+2,p.y+2,TOKEN_R,0,Math.PI*2); ctx.fill();
      ctx.fillStyle=(mode==="defend"&&t.type!=="taker")?"#8b2a2a":rs.color;
      ctx.beginPath(); ctx.arc(p.x,p.y,TOKEN_R,0,Math.PI*2); ctx.fill();
      ctx.strokeStyle=isSel?"#FFD700":"rgba(255,255,255,0.8)"; ctx.lineWidth=isSel?3:1.8;
      ctx.beginPath(); ctx.arc(p.x,p.y,TOKEN_R,0,Math.PI*2); ctx.stroke();
      if(isSel){ctx.strokeStyle="rgba(212,148,26,0.35)"; ctx.lineWidth=6; ctx.beginPath(); ctx.arc(p.x,p.y,TOKEN_R+5,0,Math.PI*2); ctx.stroke();}
      ctx.fillStyle="#fff"; ctx.font="bold 11px DM Sans"; ctx.textAlign="center"; ctx.textBaseline="middle";
      ctx.fillText(t.num||"?",p.x,p.y);
      if(t.name){
        const lw=Math.min(t.name.split(" ")[0].length*7+10,76);
        ctx.fillStyle="rgba(0,0,0,0.62)"; ctx.beginPath();
        ctx.roundRect(p.x-lw/2,p.y+TOKEN_R+3,lw,15,3); ctx.fill();
        ctx.fillStyle="#fff"; ctx.font="bold 8px DM Sans";
        ctx.fillText(t.name.split(" ")[0],p.x,p.y+TOKEN_R+10);
      }
      ctx.fillStyle="rgba(255,255,255,0.7)"; ctx.font="7px DM Sans";
      ctx.fillText(rs.label.split(" ")[0],p.x,p.y-TOKEN_R-4);
    });

    if(arrowStart){const p=px(arrowStart.x,arrowStart.y); ctx.fillStyle="rgba(212,148,26,0.55)"; ctx.beginPath(); ctx.arc(p.x,p.y,8,0,Math.PI*2); ctx.fill();}
  },[tokens,arrows,zone,selTok,arrowStart,size,spType,mode]);

  const toP=useCallback((cx,cy)=>{
    const cv=canvasRef.current; if(!cv) return{x:0,y:0};
    const r=cv.getBoundingClientRect();
    return{x:((cx-r.left)/r.width)*100, y:((cy-r.top)/r.height)*100};
  },[]);

  const hitTok=useCallback((xp,yp)=>{
    const {w,h}=size;
    return tokens.find(t=>{const dx=((t.x-xp)/100)*w, dy=((t.y-yp)/100)*h; return Math.sqrt(dx*dx+dy*dy)<TOKEN_R+6;});
  },[tokens,size]);

  const onMouseDown=useCallback(e=>{
    if(tool!=="move") return;
    const {x,y}=toP(e.clientX,e.clientY);
    const hit=hitTok(x,y); if(!hit) return;
    setSelTok(hit.id); setDragging(false);
    const onM=me=>{
      setDragging(true);
      const {x:nx,y:ny}=toP(me.clientX,me.clientY);
      setTokens(prev=>prev.map(t=>t.id===hit.id?{...t,x:Math.max(2,Math.min(98,nx)),y:Math.max(2,Math.min(98,ny))}:t));
    };
    const onU=()=>{setTimeout(()=>setDragging(false),50); document.removeEventListener("mousemove",onM); document.removeEventListener("mouseup",onU);};
    document.addEventListener("mousemove",onM); document.addEventListener("mouseup",onU);
  },[tool,hitTok,toP]);

  const onClick=useCallback(e=>{
    if(dragging) return;
    const {x,y}=toP(e.clientX,e.clientY);
    if(tool==="runner"){
      const nn=tokens.length>0?Math.max(...tokens.map(t=>t.num||0))+1:1;
      const nt={id:Date.now(),x,y,type:selRunner,num:nn,name:""};
      setTokens(p=>[...p,nt]); setSelTok(nt.id);
    } else if(tool==="arrow"){
      if(!arrowStart){setArrowStart({x,y});}
      else{setArrows(p=>[...p,{id:Date.now(),x1:arrowStart.x,y1:arrowStart.y,x2:x,y2:y,color:arrowColor}]); setArrowStart(null);}
    } else if(tool==="delete"){
      const h=hitTok(x,y);
      if(h){setTokens(p=>p.filter(t=>t.id!==h.id)); if(selTok===h.id)setSelTok(null);}
      else{setArrows(p=>p.slice(0,-1));}
    } else {
      const h=hitTok(x,y); setSelTok(h?h.id:null);
    }
  },[tool,selRunner,arrowStart,arrowColor,tokens,hitTok,dragging,toP,selTok]);

  const selTokData=tokens.find(t=>t.id===selTok);
  const myRoutines=routines.filter(r=>r.type===spType);

  const loadRoutine=r=>{
    setTokens(r.tokens.map(t=>({...t}))); setArrows(r.arrows.map(a=>({...a})));
    setZone(r.deliveryZone||null); setSpType(r.type); setMode(r.mode||"attack");
    setActiveId(r.id); setRName(r.name); setRDesc(r.desc||""); setSelTok(null); setArrowStart(null);
  };

  const clearBoard=()=>{setTokens([]); setArrows([]); setZone(null); setSelTok(null); setArrowStart(null); setActiveId(null); setRName(""); setRDesc("");};

  const saveRoutine=()=>{
    if(!rName) return;
    const r={id:activeId||`r-${Date.now()}`,name:rName,desc:rDesc,type:spType,mode,tokens:tokens.map(t=>({...t})),arrows:arrows.map(a=>({...a})),deliveryZone:zone};
    const upd=[r,...routines.filter(x=>x.id!==r.id)];
    setRoutines(upd); saveToStorage("sp_routines",upd); setActiveId(r.id);
    setSyncStatus("saving");
    saveRoutinesToCloud(upd).then(()=>{ setSyncStatus("saved"); setTimeout(()=>setSyncStatus(""),2000); });
  };

  const exportRoutine=()=>{
    const lines=[`SET PIECE ROUTINE — ${rName||"Untitled"}`,`Type: ${SP_TYPES.find(t=>t.id===spType)?.label} · ${mode==="attack"?"Attacking":"Defensive"}`,`Delivery: ${zone||"Not set"}`,``,`RUNNERS:`,
      ...tokens.map(t=>`  #${t.num} ${t.name||"Player"} — ${rStyle(t.type).label}: ${rStyle(t.type).desc}`),
      ``,rDesc?`NOTES:\n${rDesc}`:""];
    const blob=new Blob([lines.filter(Boolean).join("\n")],{type:"text/plain"});
    const url=URL.createObjectURL(blob);
    const a=document.createElement("a"); a.href=url; a.download=`setpiece-${(rName||"routine").replace(/\s+/g,"-")}.txt`; a.click(); URL.revokeObjectURL(url);
  };

  const exportRoutinePDF=()=>{
    const routine={name:rName,type:spType,mode,tokens,arrows,deliveryZone:zone,desc:rDesc};
    exportSetPiecePDF(routine);
  };

  const zones=DELIVERY_ZONES[spType]||[];

  return(
    <>
      <style>{SP_CSS}</style>
      <div className="sp-wrap">

        {/* LEFT */}
        <div className="sp-left">
          <div className="sp-left-header">
            <div className="sp-left-title">Set Piece Builder</div>
            <div className="sp-left-sub">Design · Assign · Save · Export</div>
          </div>

          <div className="sp-section">
            <div className="sp-section-title">Set Piece Type</div>
            <div className="sp-grid-2">
              {SP_TYPES.map(t=><button key={t.id} className={`sp-btn ${spType===t.id?"on":""}`} onClick={()=>{setSpType(t.id);clearBoard();}}>{t.icon} {t.label}</button>)}
            </div>
          </div>

          <div className="sp-section">
            <div className="sp-section-title">Mode</div>
            <div className="sp-mode-row">
              <div className={`sp-mode-btn ${mode==="attack"?"atk":""}`} onClick={()=>{setMode("attack");clearBoard();}}>⚔ Attack</div>
              <div className={`sp-mode-btn ${mode==="defend"?"def":""}`} onClick={()=>{setMode("defend");clearBoard();}}>🛡 Defend</div>
            </div>
          </div>

          <div className="sp-section" style={{flex:"0 0 auto",maxHeight:"260px",overflowY:"auto"}}>
            <div className="sp-section-title">Runner Type</div>
            {RUNNER_TYPES.map(r=>(
              <div key={r.id} className={`sp-runner-row ${selRunner===r.id?"on":""}`}
                style={selRunner===r.id?{borderLeftColor:r.color,borderLeftWidth:3}:{}}
                onClick={()=>{setSelRunner(r.id);setTool("runner");}}>
                <div className="sp-runner-dot" style={{background:r.color}}/>
                <div><div className="sp-runner-label">{r.label}</div><div className="sp-runner-sub">{r.desc}</div></div>
              </div>
            ))}
          </div>

          <div style={{padding:"0.6rem 1rem 0.3rem",borderBottom:"1px solid var(--rule)",flexShrink:0}}>
            <div className="sp-section-title">Saved Routines ({myRoutines.length})</div>
          </div>
          <div className="sp-routine-scroll">
            {myRoutines.length===0&&<div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.7rem",color:"var(--muted)",fontStyle:"italic",padding:"0.5rem"}}>No routines saved yet. Design one and save it below.</div>}
            {myRoutines.map(r=>(
              <div key={r.id} className={`sp-routine-item ${activeId===r.id?"active":""}`} onClick={()=>loadRoutine(r)}>
                <div style={{flex:1}}><div className="sp-routine-name">{r.name}</div><div className="sp-routine-meta">{r.tokens?.length||0} runners · {r.mode==="attack"?"Atk":"Def"}</div></div>
                <span className={`sp-pos-badge ${r.mode==="attack"?"att":"def"}`} style={{fontSize:"0.5rem",padding:"0.08rem 0.35rem"}}>{r.mode==="attack"?"ATK":"DEF"}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CANVAS */}
        <div className="sp-canvas-area">
          <div className="sp-toolbar">
            <div className="sp-tb-grp">
              <span className="sp-tb-lbl">Tool</span>
              {[{id:"move",l:"✋ Move"},{id:"runner",l:"+ Runner"},{id:"arrow",l:"↗ Arrow"},{id:"delete",l:"✕ Del"}].map(t=>(
                <button key={t.id} className={`sp-tb-b ${tool===t.id?"on":""}`} onClick={()=>{setTool(t.id);setArrowStart(null);}}>{t.l}</button>
              ))}
            </div>
            {tool==="arrow"&&(
              <div className="sp-tb-grp">
                <span className="sp-tb-lbl">Colour</span>
                {["#FFD700","#4ade80","#f87171","#60a5fa","#ffffff","#fb923c"].map(c=>(
                  <div key={c} onClick={()=>setArrowColor(c)} style={{width:16,height:16,background:c,border:`2px solid ${arrowColor===c?"white":"rgba(255,255,255,0.2)"}`,cursor:"pointer",borderRadius:"2px"}}/>
                ))}
                {arrowStart&&<span style={{fontSize:"0.6rem",color:"var(--mustard)",fontWeight:700}}>→ Click end point</span>}
              </div>
            )}
            <div className="sp-tb-grp">
              <span className="sp-tb-lbl">Clear</span>
              <button className="sp-tb-b red" onClick={()=>setArrows([])}>Arrows</button>
              <button className="sp-tb-b red" onClick={clearBoard}>All</button>
            </div>
            <div className="sp-tb-grp">
              <button className="sp-tb-b" onClick={exportRoutine}>⬇ Export</button>
            </div>
          </div>
          <div className="sp-pitch-wrap" ref={wrapRef}>
            <canvas ref={canvasRef} className="sp-canvas" onClick={onClick} onMouseDown={onMouseDown}
              style={{cursor:tool==="move"?"default":"crosshair"}}/>
          </div>
        </div>

        {/* RIGHT */}
        <div className="sp-right">
          <div className="sp-right-head">{selTokData?`Runner #${selTokData.num}`:"Routine & Zones"}</div>
          <div className="sp-right-body">

            {selTokData&&(
              <div style={{background:"var(--teal4)",border:"1.5px solid var(--teal)",borderLeft:"4px solid var(--teal)",padding:"0.85rem",marginBottom:"0.85rem"}}>
                <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"0.95rem",letterSpacing:"1.5px",color:"var(--ink)",marginBottom:"0.5rem",display:"flex",alignItems:"center",gap:"0.5rem"}}>
                  <span style={{width:18,height:18,borderRadius:"50%",background:rStyle(selTokData.type).color,display:"inline-block"}}/>
                  {rStyle(selTokData.type).label}
                </div>
                <label className="sp-r-label">Player Name</label>
                <input className="sp-r-input" placeholder="e.g. Marcus Webb" value={selTokData.name||""} onChange={e=>setTokens(p=>p.map(t=>t.id===selTokData.id?{...t,name:e.target.value}:t))}/>
                <label className="sp-r-label">Number</label>
                <input className="sp-r-input" type="number" min="1" max="99" value={selTokData.num||""} onChange={e=>setTokens(p=>p.map(t=>t.id===selTokData.id?{...t,num:Number(e.target.value)}:t))}/>
                <label className="sp-r-label">Runner Type</label>
                <select className="sp-r-select" value={selTokData.type} onChange={e=>setTokens(p=>p.map(t=>t.id===selTokData.id?{...t,type:e.target.value}:t))}>
                  {RUNNER_TYPES.map(r=><option key={r.id} value={r.id}>{r.label}</option>)}
                </select>
                {squad.length>0&&(<>
                  <label className="sp-r-label">Assign Squad Player</label>
                  <select className="sp-r-select" value={selTokData.name||""} onChange={e=>setTokens(p=>p.map(t=>t.id===selTokData.id?{...t,name:e.target.value}:t))}>
                    <option value="">— Pick player —</option>
                    {squad.map(p=><option key={p.id} value={p.name}>{p.name} ({p.pos})</option>)}
                  </select>
                </>)}
                <button style={{width:"100%",marginTop:"0.6rem",padding:"0.42rem",background:"var(--darkred)",color:"var(--white)",border:"none",fontFamily:"'DM Sans',sans-serif",fontSize:"0.62rem",fontWeight:800,letterSpacing:"1.5px",textTransform:"uppercase",cursor:"pointer"}}
                  onClick={()=>{setTokens(p=>p.filter(t=>t.id!==selTokData.id));setSelTok(null);}}>
                  Remove Runner
                </button>
              </div>
            )}

            <label className="sp-r-label" style={{marginTop:0}}>Delivery Zone</label>
            {zones.map(z=>(
              <div key={z.id} className={`sp-zone-opt ${zone===z.id?"on":""}`} onClick={()=>setZone(zone===z.id?null:z.id)}>
                <div style={{paddingTop:2}}>
                  <div className="sp-zone-title">{zone===z.id?"✓ ":""}{z.label}</div>
                  <div className="sp-zone-desc">{z.desc}</div>
                </div>
              </div>
            ))}

            {tokens.length>0&&(
              <>
                <label className="sp-r-label">Runners on Board</label>
                {tokens.map(t=>{const rs=rStyle(t.type); return(
                  <div key={t.id} className={`sp-legend-row ${selTok===t.id?"sel":""}`} onClick={()=>setSelTok(t.id)}>
                    <div className="sp-legend-dot" style={{background:rs.color}}/>
                    <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"0.82rem",letterSpacing:"1px",color:"var(--muted)",width:"20px"}}>#{t.num}</span>
                    <span style={{flex:1,fontWeight:600,fontSize:"0.75rem"}}>{t.name||rs.label}</span>
                    <span style={{fontSize:"0.58rem",color:"var(--muted)",fontStyle:"italic"}}>{rs.label}</span>
                  </div>
                );})}
              </>
            )}

            <label className="sp-r-label">Summary</label>
            {[{l:"Type",v:SP_TYPES.find(t=>t.id===spType)?.label},{l:"Mode",v:mode==="attack"?"Attacking":"Defensive"},{l:"Runners",v:String(tokens.length)},{l:"Arrows",v:String(arrows.length)},{l:"Delivery",v:zone||"Not set"}].map((r,i)=>(
              <div key={i} className="sp-sum-row"><span className="sp-sum-lbl">{r.l}</span><span className="sp-sum-val">{r.v}</span></div>
            ))}

            <div className="sp-save-box">
              <label className="sp-r-label" style={{marginTop:0}}>
                Save Routine
                {syncStatus==="saving"&&<span style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.58rem",color:"var(--mustard)",fontWeight:700,marginLeft:"0.5rem",letterSpacing:"1px"}}>Saving…</span>}
                {syncStatus==="saved"&&<span style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.58rem",color:"#4ade80",fontWeight:700,marginLeft:"0.5rem",letterSpacing:"1px"}}>✓ Synced</span>}
              </label>
              <input className="sp-save-inp" placeholder="Routine name e.g. Near Post Flick" value={rName} onChange={e=>setRName(e.target.value)}/>
              <input className="sp-save-inp" placeholder="Description (optional)" value={rDesc} onChange={e=>setRDesc(e.target.value)}/>
              <button className="sp-save-btn" onClick={saveRoutine}>💾 Save to Cloud</button>
              <button className="sp-export-btn" onClick={exportRoutine}>⬇ Text File</button>
              <ExportButton onClick={exportRoutinePDF} label="Print / PDF" small/>
            </div>

          </div>
        </div>
      </div>
    </>
  );
}
